import { Helmet } from "@zudoku/react-helmet-async";
import { Link } from "react-router";

type LandingPageProps = {
  locale?: "en" | "pt";
};

export function LandingPage({ locale = "en" }: LandingPageProps) {
  const isPortuguese = locale === "pt";

  return (
    <>
      <Helmet>
        <title>{isPortuguese ? "Documentação IZI Pay" : "Welcome"}</title>
        <meta
          name="description"
          content={
            isPortuguese
              ? "Documentação da API IZI Pay - guias, referência OpenAPI e catálogo."
              : "IZI Pay API documentation - guides, OpenAPI reference, and catalog."
          }
        />
      </Helmet>

      <div className="mx-auto max-w-6xl pb-10 pt-2 md:pt-6">
        <div className="grid items-center gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(280px,420px)] lg:gap-16">
          <div className="space-y-8">
            <h1 className="text-balance text-4xl font-bold tracking-tight text-foreground md:text-5xl lg:text-[3.25rem] lg:leading-[1.1]">
              {isPortuguese
                ? "Construa experiências de pagamento com "
                : "Build payments experiences with "}
              <span className="rounded-xl bg-primary px-3 py-1 text-primary-foreground shadow-sm">
                {isPortuguese ? "confiança" : "confidence"}
              </span>
            </h1>

            <div className="space-y-4 text-lg leading-relaxed text-muted-foreground">
              <p>
                {isPortuguese ? "Explore a " : "Welcome to the "}
                <a
                  href="https://izipay.ao"
                  data-discover="true"
                  className="underline underline-offset-4 transition-colors hover:text-primary"
                >
                  IZI Pay
                </a>{" "}
                {isPortuguese
                  ? "documentação da API com guias, referência interativa e catálogo de APIs para Identidade, Faturação, Pagamentos e operações de terminal."
                  : "API documentation. Explore guides, interactive reference, and our catalog of APIs for Identity, Billing, Payments, and terminal operations."}
              </p>
              <p>
                {isPortuguese
                  ? "Comece pela introdução para conceitos e configuração, avance para a referência da API para detalhes dos endpoints, ou navegue pelo catálogo para descobrir o que pode integrar."
                  : "Start with the introduction for concepts and setup, jump into the API reference for endpoint details, or browse the catalog to discover what you can integrate."}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <Link
                to={isPortuguese ? "/pt/introduction" : "/introduction"}
                className="inline-flex h-11 items-center justify-center rounded-md bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-sm transition-opacity hover:opacity-90"
              >
                {isPortuguese ? "Começar" : "Get started"}
              </Link>
              <span className="text-sm text-muted-foreground">
                {isPortuguese ? "ou" : "or"}
              </span>
              <Link
                to={isPortuguese ? "/pt/api/payment" : "/api/payment"}
                className="inline-flex h-11 items-center justify-center rounded-md border border-border bg-transparent px-6 text-sm font-semibold text-foreground transition-colors hover:bg-muted/60"
              >
                {isPortuguese ? "Ver API de Pagamentos" : "View Payment API"}
              </Link>
            </div>
          </div>

          <div className="mx-auto w-full max-w-md lg:mx-0 lg:max-w-none">
            <img
              src="/2903544.png"
              alt="IZI Pay preview"
              // className="w-full rounded-3xl border border-border object-cover shadow-xl"
            />
          </div>
        </div>

        <footer className="mt-54 flex flex-col items-center justify-between gap-6 border-t border-border pt-6 text-sm text-muted-foreground md:flex-row">
          <div className="flex items-center gap-3">
            <img
              src="/suplitel_no_background.png"
              alt="Suplitel"
              className="h-8 w-auto dark:hidden"
            />
            <img
              src="/suplitel_white.png"
              alt="Suplitel"
              className="hidden h-8 w-auto dark:block"
            />
          </div>

          <div className="text-center">
            {isPortuguese
              ? "© 2026 Suplitel Lda. Todos os direitos reservados."
              : "© 2026 Suplitel Lda. All rights reserved."}
          </div>

          <div className="flex items-center gap-4">
            <a
              href="https://github.com"
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background text-foreground transition-colors hover:border-primary hover:text-primary"
              aria-label="GitHub"
            >
              <svg
                viewBox="0 0 1024 1024"
                aria-hidden="true"
                className="h-4 w-4"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8 0C3.58 0 0 3.58 0 8C0 11.54 2.29 14.53 5.47 15.59C5.87 15.66 6.02 15.42 6.02 15.21C6.02 15.02 6.01 14.39 6.01 13.72C4 14.09 3.48 13.23 3.32 12.78C3.23 12.55 2.84 11.84 2.5 11.65C2.22 11.5 1.82 11.13 2.49 11.12C3.12 11.11 3.57 11.7 3.72 11.94C4.44 13.15 5.59 12.81 6.05 12.6C6.12 12.08 6.33 11.73 6.56 11.53C4.78 11.33 2.92 10.64 2.92 7.58C2.92 6.71 3.23 5.99 3.74 5.43C3.66 5.23 3.38 4.41 3.82 3.31C3.82 3.31 4.49 3.1 6.02 4.13C6.66 3.95 7.34 3.86 8.02 3.86C8.7 3.86 9.38 3.95 10.02 4.13C11.55 3.09 12.22 3.31 12.22 3.31C12.66 4.41 12.38 5.23 12.3 5.43C12.81 5.99 13.12 6.7 13.12 7.58C13.12 10.65 11.25 11.33 9.47 11.53C9.76 11.78 10.01 12.26 10.01 13.01C10.01 14.08 10 14.94 10 15.21C10 15.42 10.15 15.67 10.55 15.59C13.71 14.53 16 11.53 16 8C16 3.58 12.42 0 8 0Z"
                  transform="scale(64)"
                />
              </svg>
            </a>

            <a
              href="https://x.com"
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background text-foreground transition-colors hover:border-primary hover:text-primary"
              aria-label="X (Twitter)"
            >
              <svg
                viewBox="0 0 1200 1227"
                aria-hidden="true"
                className="h-4 w-4"
                fill="currentColor"
              >
                <path d="M714.163 519.284 1160.89 0h-105.86L667.137 450.887 357.328 0H0l468.492 681.821L0 1226.37h105.866l409.625-476.152 327.181 476.152H1200L714.137 519.284h.026ZM569.165 687.828l-47.468-67.894-377.686-540.24h162.604l304.797 435.991 47.468 67.894 396.2 566.721H892.476L569.165 687.854v-.026Z" />
              </svg>
            </a>

            <a
              href="https://instagram.com"
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background text-foreground transition-colors hover:border-primary hover:text-primary"
              aria-label="Instagram"
            >
              <svg
                viewBox="0 0 264.583 264.583"
                aria-hidden="true"
                className="h-4 w-4"
              >
                <defs>
                  <radialGradient
                    id="instagram_icon__f"
                    cx="158.429"
                    cy="578.088"
                    r="52.352"
                    fx="158.429"
                    fy="578.088"
                    gradientTransform="matrix(0 -4.03418 4.28018 0 -2332.227 942.236)"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop offset="0" stopColor="#fc0" />
                    <stop offset=".124" stopColor="#fc0" />
                    <stop offset=".567" stopColor="#fe4a05" />
                    <stop offset=".694" stopColor="#ff0f3f" />
                    <stop offset="1" stopColor="#fe0657" stopOpacity="0" />
                  </radialGradient>
                  <radialGradient
                    id="instagram_icon__g"
                    cx="172.615"
                    cy="600.692"
                    r="65"
                    fx="172.615"
                    fy="600.692"
                    gradientTransform="matrix(.67441 -1.16203 1.51283 .87801 -814.366 -47.835)"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop offset="0" stopColor="#fc0" />
                    <stop offset="1" stopColor="#fc0" stopOpacity="0" />
                  </radialGradient>
                  <radialGradient
                    id="instagram_icon__h"
                    cx="144.012"
                    cy="51.337"
                    r="67.081"
                    fx="144.012"
                    fy="51.337"
                    gradientTransform="matrix(-2.3989 .67549 -.23008 -.81732 464.996 -26.404)"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop offset="0" stopColor="#780cff" />
                    <stop offset="1" stopColor="#820bff" stopOpacity="0" />
                  </radialGradient>
                  <radialGradient
                    id="instagram_icon__e"
                    cx="199.788"
                    cy="628.438"
                    r="52.352"
                    fx="199.788"
                    fy="628.438"
                    gradientTransform="matrix(-3.10797 .87652 -.6315 -2.23914 1345.65 1374.198)"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop offset="0" stopColor="#ff005f" />
                    <stop offset="1" stopColor="#fc01d8" />
                  </radialGradient>
                </defs>
                <path
                  fill="url(#instagram_icon__e)"
                  d="M204.15 18.143c-55.23 0-71.383.057-74.523.317-11.334.943-18.387 2.728-26.07 6.554-5.922 2.942-10.592 6.351-15.201 11.13-8.394 8.716-13.481 19.439-15.323 32.184-.895 6.188-1.156 7.45-1.209 39.056-.02 10.536 0 24.4 0 42.999 0 55.2.062 71.341.326 74.476.916 11.032 2.645 17.973 6.308 25.565 7 14.533 20.37 25.443 36.12 29.514 5.453 1.404 11.476 2.178 19.208 2.544 3.277.142 36.669.244 70.081.244 33.413 0 66.826-.04 70.02-.203 8.954-.422 14.153-1.12 19.901-2.606 15.852-4.09 28.977-14.838 36.12-29.575 3.591-7.409 5.412-14.614 6.236-25.07.18-2.28.255-38.626.255-74.924 0-36.304-.082-72.583-.26-74.863-.835-10.625-2.656-17.77-6.364-25.32-3.042-6.182-6.42-10.799-11.324-15.519-8.752-8.361-19.455-13.45-32.21-15.29-6.18-.894-7.41-1.158-39.033-1.213z"
                  transform="translate(-71.816 -18.143)"
                />
                <path
                  fill="url(#instagram_icon__f)"
                  d="M204.15 18.143c-55.23 0-71.383.057-74.523.317-11.334.943-18.387 2.728-26.07 6.554-5.922 2.942-10.592 6.351-15.201 11.13-8.394 8.716-13.481 19.439-15.323 32.184-.895 6.188-1.156 7.45-1.209 39.056-.02 10.536 0 24.4 0 42.999 0 55.2.062 71.341.326 74.476.916 11.032 2.645 17.973 6.308 25.565 7 14.533 20.37 25.443 36.12 29.514 5.453 1.404 11.476 2.178 19.208 2.544 3.277.142 36.669.244 70.081.244 33.413 0 66.826-.04 70.02-.203 8.954-.422 14.153-1.12 19.901-2.606 15.852-4.09 28.977-14.838 36.12-29.575 3.591-7.409 5.412-14.614 6.236-25.07.18-2.28.255-38.626.255-74.924 0-36.304-.082-72.583-.26-74.863-.835-10.625-2.656-17.77-6.364-25.32-3.042-6.182-6.42-10.799-11.324-15.519-8.752-8.361-19.455-13.45-32.21-15.29-6.18-.894-7.41-1.158-39.033-1.213z"
                  transform="translate(-71.816 -18.143)"
                />
                <path
                  fill="url(#instagram_icon__g)"
                  d="M204.15 18.143c-55.23 0-71.383.057-74.523.317-11.334.943-18.387 2.728-26.07 6.554-5.922 2.942-10.592 6.351-15.201 11.13-8.394 8.716-13.481 19.439-15.323 32.184-.895 6.188-1.156 7.45-1.209 39.056-.02 10.536 0 24.4 0 42.999 0 55.2.062 71.341.326 74.476.916 11.032 2.645 17.973 6.308 25.565 7 14.533 20.37 25.443 36.12 29.514 5.453 1.404 11.476 2.178 19.208 2.544 3.277.142 36.669.244 70.081.244 33.413 0 66.826-.04 70.02-.203 8.954-.422 14.153-1.12 19.901-2.606 15.852-4.09 28.977-14.838 36.12-29.575 3.591-7.409 5.412-14.614 6.236-25.07.18-2.28.255-38.626.255-74.924 0-36.304-.082-72.583-.26-74.863-.835-10.625-2.656-17.77-6.364-25.32-3.042-6.182-6.42-10.799-11.324-15.519-8.752-8.361-19.455-13.45-32.21-15.29-6.18-.894-7.41-1.158-39.033-1.213z"
                  transform="translate(-71.816 -18.143)"
                />
                <path
                  fill="url(#instagram_icon__h)"
                  d="M204.15 18.143c-55.23 0-71.383.057-74.523.317-11.334.943-18.387 2.728-26.07 6.554-5.922 2.942-10.592 6.351-15.201 11.13-8.394 8.716-13.481 19.439-15.323 32.184-.895 6.188-1.156 7.45-1.209 39.056-.02 10.536 0 24.4 0 42.999 0 55.2.062 71.341.326 74.476.916 11.032 2.645 17.973 6.308 25.565 7 14.533 20.37 25.443 36.12 29.514 5.453 1.404 11.476 2.178 19.208 2.544 3.277.142 36.669.244 70.081.244 33.413 0 66.826-.04 70.02-.203 8.954-.422 14.153-1.12 19.901-2.606 15.852-4.09 28.977-14.838 36.12-29.575 3.591-7.409 5.412-14.614 6.236-25.07.18-2.28.255-38.626.255-74.924 0-36.304-.082-72.583-.26-74.863-.835-10.625-2.656-17.77-6.364-25.32-3.042-6.182-6.42-10.799-11.324-15.519-8.752-8.361-19.455-13.45-32.21-15.29-6.18-.894-7.41-1.158-39.033-1.213z"
                  transform="translate(-71.816 -18.143)"
                />
                <path
                  fill="#fff"
                  d="M132.345 33.973c-26.716 0-30.07.117-40.563.594-10.472.48-17.62 2.136-23.876 4.567-6.47 2.51-11.958 5.87-17.426 11.335-5.472 5.464-8.834 10.948-11.354 17.412-2.44 6.252-4.1 13.397-4.57 23.858-.47 10.486-.593 13.838-.593 40.535 0 26.697.119 30.037.594 40.522.482 10.465 2.14 17.609 4.57 23.859 2.515 6.465 5.876 11.95 11.346 17.414 5.466 5.468 10.955 8.834 17.42 11.345 6.26 2.431 13.41 4.088 23.881 4.567 10.493.477 13.844.594 40.559.594 26.719 0 30.061-.117 40.555-.594 10.472-.48 17.63-2.136 23.888-4.567 6.468-2.51 11.948-5.877 17.414-11.345 5.472-5.464 8.834-10.949 11.354-17.412 2.419-6.252 4.079-13.398 4.57-23.858.472-10.486.595-13.828.595-40.525s-.123-30.047-.594-40.533c-.492-10.465-2.152-17.608-4.57-23.858-2.521-6.466-5.883-11.95-11.355-17.414-5.472-5.468-10.944-8.827-17.42-11.335-6.271-2.431-13.424-4.088-23.897-4.567-10.493-.477-13.834-.594-40.558-.594zm-8.825 17.715c2.62-.004 5.542 0 8.825 0 26.266 0 29.38.094 39.752.565 9.591.438 14.797 2.04 18.264 3.385 4.591 1.782 7.864 3.912 11.305 7.352 3.443 3.44 5.575 6.717 7.362 11.305 1.346 3.46 2.951 8.663 3.388 18.247.47 10.363.573 13.475.573 39.71 0 26.233-.102 29.346-.573 39.709-.44 9.584-2.042 14.786-3.388 18.247-1.783 4.587-3.919 7.854-7.362 11.292-3.443 3.441-6.712 5.57-11.305 7.352-3.463 1.352-8.673 2.95-18.264 3.388-10.37.47-13.486.573-39.752.573-26.268 0-29.38-.102-39.751-.573-9.592-.443-14.797-2.044-18.267-3.39-4.59-1.781-7.87-3.911-11.313-7.352-3.443-3.44-5.574-6.709-7.362-11.298-1.346-3.461-2.95-8.663-3.387-18.247-.472-10.363-.566-13.476-.566-39.726s.094-29.347.566-39.71c.438-9.584 2.04-14.786 3.387-18.25 1.783-4.588 3.919-7.865 7.362-11.305 3.443-3.441 6.722-5.57 11.313-7.357 3.468-1.351 8.675-2.949 18.267-3.389 9.075-.41 12.592-.532 30.926-.553zm61.337 16.322c-6.518 0-11.805 5.277-11.805 11.792 0 6.512 5.287 11.796 11.805 11.796 6.517 0 11.804-5.284 11.804-11.796 0-6.513-5.287-11.796-11.805-11.796zm-52.512 13.782c-27.9 0-50.519 22.603-50.519 50.482 0 27.879 22.62 50.471 50.52 50.471s50.51-22.592 50.51-50.471c0-27.879-22.613-50.482-50.513-50.482zm0 17.715c18.11 0 32.792 14.67 32.792 32.767 0 18.096-14.683 32.767-32.792 32.767-18.11 0-32.791-14.671-32.791-32.767 0-18.098 14.68-32.767 32.791-32.767z"
                />
              </svg>
            </a>
          </div>
        </footer>
      </div>
    </>
  );
}
